import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/shared/lib/cn'

interface KineticTextProps {
  children: string
  className?: string
  /** Delay before the first word reveals (sec). */
  delay?: number
  /** Stagger between words (sec). Default 0.08. */
  stagger?: number
  /** As which element to render. Default 'span'. */
  as?: 'span' | 'div' | 'h1' | 'h2' | 'h3'
}

/**
 * Word-by-word reveal — the kinetic typography pattern from
 * `ui-ux-pro-max` for hero headlines (§6.7.5 motion budget).
 *
 * Each word fades + lifts into place on mount. Reduced-motion users see
 * the final, static state. Use sparingly — best on a single hero per route.
 */
export function KineticText({
  children,
  className,
  delay = 0.05,
  stagger = 0.08,
  as = 'span',
}: KineticTextProps) {
  const reducedMotion = useReducedMotion()
  const Component = motion[as]
  const words = children.split(/(\s+)/)

  if (reducedMotion) {
    const Tag = as
    return <Tag className={className}>{children}</Tag>
  }

  return (
    <Component
      className={cn('inline-block', className)}
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {words.map((w, i) =>
        /\s/.test(w) ? (
          <span key={i}>{w}</span>
        ) : (
          <motion.span
            key={i}
            className="inline-block"
            variants={{
              hidden: { y: '110%', opacity: 0, filter: 'blur(8px)' },
              show: {
                y: 0,
                opacity: 1,
                filter: 'blur(0px)',
                transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
              },
            }}
            style={{ display: 'inline-block', willChange: 'transform' }}
          >
            {w}
          </motion.span>
        ),
      )}
    </Component>
  )
}
